/**
 * 公告文案按当前 UI 语言选取。
 * 优先级：当前 locale → en → zh → display* → 扁平字段
 * 正文不 trim；页脚发稿人/发稿日与正文分离。
 */

export type AnnouncementLocaleFields = {
  title?: string | null;
  body?: string | null;
  titleEn?: string | null;
  bodyEn?: string | null;
  author?: string | null;
  authorEn?: string | null;
  signedOn?: string | null;
  displayTitle?: string | null;
  displayBody?: string | null;
  displayAuthor?: string | null;
  displaySignedOn?: string | null;
  locale?: string | null;
  translations?: Record<
    string,
    {
      title?: string | null;
      body?: string | null;
      author?: string | null;
    } | null | undefined
  > | null;
};

export type AnnouncementCopy = {
  title: string;
  /** 原文保留空白，展示用 pre-wrap，勿再 trim */
  body: string;
  author: string;
  /** 原始 YYYY-MM-DD */
  signedOn: string;
  /** 按 UI 语言格式化后的发稿日 */
  signedOnLabel: string;
};

const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function normKey(raw: string | null | undefined): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

/** 接口 lang 仅 zh|en：中文族 → zh，其余 → en */
export function announcementApiLang(uiLocale?: string | null): 'zh' | 'en' {
  return normKey(uiLocale).startsWith('zh') ? 'zh' : 'en';
}

function pickTrimmed(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    const s = String(c ?? '').trim();
    if (s) return s;
  }
  return '';
}

/** 正文：有内容则原样返回（不 trim） */
function pickBodyRaw(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c);
    if (s.trim().length > 0) return s;
  }
  return '';
}

/** 生成候选语言码：当前 → 语种主干 → en → zh（去重） */
export function announcementLocaleCandidates(uiLocale: string): string[] {
  const full = normKey(uiLocale);
  const base = full.split('-')[0] || '';
  const ordered = [full, base, 'en', 'zh'];
  const out: string[] = [];
  for (const k of ordered) {
    if (!k) continue;
    if (k === 'zh-cn' || k === 'zh-hans') {
      if (!out.includes('zh')) out.push('zh');
      continue;
    }
    if (k === 'zh-tw' || k === 'zh-hk' || k === 'zh-hant' || k === 'zh-mo') {
      if (!out.includes(k)) out.push(k);
      if (!out.includes('zh')) out.push('zh');
      continue;
    }
    if (!out.includes(k)) out.push(k);
  }
  if (!out.includes('en')) out.push('en');
  if (!out.includes('zh')) out.push('zh');
  return out;
}

type TranslationHit = { title: string; body: string; author: string };

function fromTranslations(
  item: AnnouncementLocaleFields,
  code: string
): TranslationHit | null {
  const map = item.translations;
  if (!map || typeof map !== 'object') return null;
  const entry =
    map[code] ||
    map[code.toLowerCase()] ||
    (code === 'zh' ? map['zh-CN'] || map['zh-cn'] : undefined) ||
    (code === 'en' ? map['en-US'] || map['en-us'] : undefined);
  if (!entry || typeof entry !== 'object') return null;
  const title = pickTrimmed(entry.title);
  const body = pickBodyRaw(entry.body);
  const author = pickTrimmed(entry.author);
  if (!title && !body && !author) return null;
  return { title, body, author };
}

/**
 * 发稿日 YYYY-MM-DD → 中文「2026年10月13日」/ 英文「October 13, 2026」
 */
export function formatAnnouncementSignedOn(
  signedOn: string | null | undefined,
  uiLocale: string
): string {
  const raw = String(signedOn || '').trim();
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (!m) return raw;
  const year = m[1];
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!month || month > 12 || !day || day > 31) return raw;
  if (normKey(uiLocale).startsWith('zh')) {
    return `${year}年${month}月${day}日`;
  }
  return `${EN_MONTHS[month - 1]} ${day}, ${year}`;
}

/**
 * 按 UI 语言解析公告标题/正文/发稿人；日期本地格式化，不必重请求。
 */
export function resolveAnnouncementCopy(
  item: AnnouncementLocaleFields | null | undefined,
  uiLocale: string
): AnnouncementCopy {
  if (!item) {
    return { title: '', body: '', author: '', signedOn: '', signedOnLabel: '' };
  }

  let title = '';
  let body = '';
  let author = '';

  for (const code of announcementLocaleCandidates(uiLocale)) {
    const hit = fromTranslations(item, code);
    if (!hit) continue;
    if (!title && hit.title) title = hit.title;
    if (!body && hit.body) body = hit.body;
    if (!author && hit.author) author = hit.author;
    if (title && body && author) break;
  }

  const preferEn = announcementApiLang(uiLocale) === 'en';

  if (!title || !body) {
    if (preferEn) {
      if (!title) title = pickTrimmed(item.titleEn);
      if (!body) body = pickBodyRaw(item.bodyEn);
    }
  }

  if (!title) title = pickTrimmed(item.displayTitle);
  if (!body) body = pickBodyRaw(item.displayBody);
  if (!title) title = pickTrimmed(item.title, item.titleEn);
  if (!body) body = pickBodyRaw(item.body, item.bodyEn);

  if (!author) {
    author = pickTrimmed(
      item.displayAuthor,
      preferEn ? item.authorEn : item.author,
      item.author,
      item.authorEn
    );
  }

  const signedOn = pickTrimmed(item.signedOn, item.displaySignedOn);
  const signedOnLabel = formatAnnouncementSignedOn(signedOn, uiLocale);

  return { title, body, author, signedOn, signedOnLabel };
}
